/**
 * listing — the detail page of one home.
 * Webflow export: detail_homes.html · webpack entry: `listing`.
 * Map: NeighborhoodMap with its layers off — one labelled pin for this home.
 *
 * The index page that lists every home is `src/pages/listing-index/`.
 */
import { onReady } from '../../shared/utils/helpers';
import { MAPBOX_TOKEN } from '../../shared/utils/variables';
import Base from '../../components/base/base';
import CookieConsent from '../../components/cookie-consent/cookie-consent';
import Header from '../../components/header/header';
import './listing.scss';
import GalleryViewer from '../../components/gallery-viewer/gallery-viewer';
import MobileGalleryViewer from '../../components/mobile-gallery-viewer/mobile-gallery-viewer';
import NeighborhoodMap from '../../components/neighborhood-map/neighborhood-map';
import BuyerIntakeForm from '../../components/buyer-intake-form/buyer-intake-form';
import CollectionData from '../../components/collection-data/collection-data';

interface ListingPageOptions {
  debug?: boolean;
  [key: string]: any;
}

class ListingPage extends Base {
  private header: Header;
  private map: NeighborhoodMap | null = null;
  private collectionData: CollectionData | null = null;

  constructor(options: ListingPageOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);
    this.debug = options.debug || false;
    
    // Initialize header with same debug setting for consistent logging
    this.header = new Header({ debug: this.debug });
    
    this.init();
  }

  init(): void {
    this.log('ListingPage initialized');

    // Cookie consent gate. Webflow holds the Meta Pixel until this grants.
    try {
      new CookieConsent({ debug: this.debug });
      this.log('CookieConsent initialized');
    } catch (e) {
      this.log('CookieConsent init failed or skipped', e);
    }

    try {
      this.collectionData = new CollectionData({ debug: this.debug });
      this.log('CollectionData initialized');
    } catch (e) {
      this.log('CollectionData init failed or skipped', e);
    }

    this.initGalleries();

    // One pin on the home's own coordinates, which Webflow binds to the
    // container as data-lat/data-long. NeighborhoodMap is the single-point
    // Mapbox component; its three context layers are for a neighborhood page,
    // so all three are off here and the pin carries the street address.
    try {
      const mapEl = document.querySelector<HTMLElement>('#property-map');
      if (mapEl) {
        // The Webflow embed ships the container hidden behind a loading block.
        // Mapbox measures the container when it creates the canvas, so this has
        // to happen before the component runs, not after it reports ready.
        const loadingEl = document.getElementById('map-loading');
        if (loadingEl) loadingEl.style.display = 'none';
        mapEl.style.display = 'block';

        // The marker is a single-line pill, so it carries the street line
        // rather than the full city/state/zip address it would have to sit on.
        const address = (mapEl.dataset.listingAddress || mapEl.dataset.address || '').trim();

        this.map = new NeighborhoodMap({
          debug: this.debug,
          selector: '#property-map',
          accessToken: MAPBOX_TOKEN,
          name: address.split(',')[0].trim(),
          amenities: false,
          walkBand: false,
          spotlight: false,
          // The map sits mid-page, so a scroll past it has to stay the page's.
          // The +/- buttons still zoom.
          scrollZoom: false
        });
        this.log('Property map initialized');
      }
    } catch (e) {
      this.log('Property map init failed or skipped', e);
    }

    // Initialize Buyer Intake Form (listing context)
    try {
      new BuyerIntakeForm({
        debug: this.debug,
        // containerSelector: '#buyer-intake-wrapper, #top-buyer-intake-form',
      });
      this.log('BuyerIntakeForm initialized');
    } catch (e) {
      this.log('BuyerIntakeForm init failed or skipped', e);
    }
  }

  /**
   * Both viewers count the grid's images at construction, and imginject
   * fills the grid after CollectionData resolves — so they wait for it.
   */
  private async initGalleries(): Promise<void> {
    if (this.collectionData) await this.collectionData.ready;

    try {
      new GalleryViewer({
        selector: '#launch-gallery',
        lightboxSelector: '.gallery-lightbox',
        contentSelector: '.gallery-lightbox__content',
        debug: this.debug
      });
    } catch (e) {
      this.log('GalleryViewer init failed or skipped', e);
    }

    // MobileGalleryViewer mounts on small screens only; a desktop viewport
    // keeps the desktop viewer alone.
    try {
      if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        // eslint-disable-next-line no-new
        new MobileGalleryViewer({ selector: '.gallery-lightbox__images', debug: this.debug });
      } else {
        this.log('MobileGalleryViewer skipped: viewport too large');
      }
    } catch (e) {
      this.log('MobileGalleryViewer init failed or skipped', e);
    }
  }
}

// Test CMS items that must not be viewable: bounce their detail URLs back to
// the index. Testing the pathname alone keeps query strings and hashes from
// affecting the match; replace() keeps the test URL out of history so Back
// still works. Runs at module load, before onReady, so nothing paints first.
const isHiddenListing = /\/(9999|8888)[^/]*\/?$/.test(window.location.pathname);
if (isHiddenListing) {
  window.location.replace('/listings');
}

onReady(() => {
  if (isHiddenListing) return;
  // You can enable debug mode by passing { debug: true }
  new ListingPage({ debug: true });
});

export default ListingPage;
